import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { sessionListHost } from '@/composition/hosts/session/session-list/session-list-host';

import { ability, ACTIONS, PERMISSION_SUBJECTS } from '@/helpers/casl/ability';

import { datadogRum } from '@datadog/browser-rum';
import { Loader2, Plus } from 'lucide-react';

import { chronicleIsRecordingAtom } from '@/atoms/chronicle';
import { externalLaunchContextAtom } from '@/atoms/externalLaunchAuthAtoms';
import { hasIntegrationAtom, integrationDataAtom } from '@/atoms/integrationAtoms';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import NavItem from '@/components/layout/Navigation/sidebar';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { useCanCreateSession } from '@/hooks/useCanCreateSession';
import { useNewSession } from '@/hooks/useNewSession';
import { useNewSessionDelegation } from '@/hooks/useShowClinicianSelector';
import { useTutorial } from '@/hooks/useTutorial';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import { type RootState } from '@/store';
import { Button } from '@heidi/design-system/button';
import { SessionPages } from '@heidi/schema/ml-scribe';
import { ClinicianSelectorPopover } from '@heidi/session';
import { t } from 'i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

interface NewSessionButtonProps {
  variant?: 'sidebar' | 'panel';
  entryPoint?: SessionCreatedEntryPoint;
  buttonVariant?: 'secondary' | 'default' | 'ghost';
  /** Panel-branch Button size only; the sidebar NavItem is unaffected. */
  size?: 'xs' | 'sm';
  /** Overrides the default sidenav.newSession label (panel/sidebar). */
  label?: string;
  /** After create+navigate, auto-start transcription on the new session. */
  autoStartTranscription?: boolean;
}

export function NewSessionButton({
  variant = 'sidebar',
  entryPoint = SessionCreatedEntryPoint.Sidebar,
  buttonVariant = 'secondary',
  size = 'sm',
  label: labelProp,
  autoStartTranscription = false,
}: NewSessionButtonProps) {
  const isFhirAuth = useIsFhirAuth();
  const isGenericEhrLaunch = Boolean(
    useAtomValue(externalLaunchContextAtom)?.ehrProvider
  );
  const isEhrLaunch = isFhirAuth || isGenericEhrLaunch;

  const { createNewSession, createNewSessionForMember, isPending, isReady } =
    useNewSession({ entryPoint, autoStartTranscription });
  const isMobile = useIsMobile();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { tutorialStepCurrentPage, onNextPage, panelOpen } = useTutorial();
  const shouldHighlightCreateNewSession =
    tutorialStepCurrentPage?.enum === SessionPages.START_SESSION && panelOpen;
  const isRunning = useSelector((rootState: RootState) => rootState.timerModel.isRunning);
  const isChronicleRecording = useAtomValue(chronicleIsRecordingAtom);
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  const canCreateSession = useCanCreateSession(isReviewingOtherUser);

  const shouldShowNewSessionButtonTooltip = useCallback(() => {
    if (!isReviewingOtherUser) {
      return !ability.can(ACTIONS.ACCESS, PERMISSION_SUBJECTS.SESSION_CREATE);
    }
    return false;
  }, [isReviewingOtherUser]);

  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const integrationData = useAtomValue(integrationDataAtom);

  // Gated on the new-session-list flag inside the hook. Admins with the
  // submenu self-create directly like any other clinician — only
  // the assistant/no-self-create-permission picker renders the selector here.
  const showClinicianSelector = useNewSessionDelegation() === 'assistantPicker';

  // Hide the button when launched from any EHR integration (Epic FHIR or
  // generic EHR launch like Healthdirect) — users are "locked" into a single
  // session.
  if (isEhrLaunch) {
    return null;
  }

  const handleClick = () => {
    datadogRum.startDurationVital(DatadogDurationVital.NEW_SESSION);
    createNewSession({ metadata: { entryPoint } });
    if (shouldHighlightCreateNewSession) onNextPage();
    if (isMobile) {
      setIsMobileHeaderSheetOpen(false);
    }
  };

  // Partner-integration sessions are locked to the launched patient — starting
  // one with no patient selected is invalid, for the plain button and the
  // delegation (clinician-selector) branch alike.
  const integrationBlocksSession =
    hasIntegration && (!integrationData.patientId || integrationData.patientId === '""');

  const isDisabled =
    !isReady ||
    isPending ||
    !canCreateSession ||
    isRunning ||
    isChronicleRecording ||
    integrationBlocksSession;

  // Creating on behalf of another clinician doesn't require self SESSION_CREATE
  // (self is filtered out of the clinician list when the admin lacks it), so we
  // drop `!canCreateSession` here — but the integration patient-lock still holds.
  const isClinicianSelectorDisabled =
    isPending || isRunning || isChronicleRecording || integrationBlocksSession;

  const label = labelProp ?? t('sidenav.newSession');

  if (showClinicianSelector) {
    return (
      <ClinicianSelectorButton
        variant={variant}
        buttonVariant={buttonVariant}
        size={size}
        label={label}
        isPending={isPending}
        isDisabled={isClinicianSelectorDisabled}
        onSelectClinician={(memberId) => {
          datadogRum.startDurationVital(DatadogDurationVital.NEW_SESSION);
          createNewSessionForMember(memberId, { metadata: { entryPoint } });
        }}
      />
    );
  }

  if (variant === 'panel') {
    return (
      <Button
        variant={buttonVariant}
        size={size}
        onClick={handleClick}
        disabled={isDisabled}
        aria-label={
          shouldShowNewSessionButtonTooltip()
            ? t('sidenav.firstSelectCliniciansSessionListToCreateASession')
            : undefined
        }
        data-testid="sessions-panel-action-new-session"
      >
        {isPending ? <Loader2 className="animate-spin" /> : <Plus aria-hidden="true" />}
        {label}
      </Button>
    );
  }

  return (
    <NavItem
      variant="action"
      label={label}
      icon={isPending ? <Loader2 className="animate-spin" /> : <Plus />}
      onClick={handleClick}
      disabled={isDisabled}
      isHighlighted={shouldHighlightCreateNewSession}
      testId="sidebar-link-new-session"
      ariaLabel={
        shouldShowNewSessionButtonTooltip()
          ? t('sidenav.firstSelectCliniciansSessionListToCreateASession')
          : undefined
      }
    />
  );
}

interface ClinicianSelectorButtonProps {
  variant: NewSessionButtonProps['variant'];
  buttonVariant: 'secondary' | 'default' | 'ghost';
  size: NewSessionButtonProps['size'];
  label: string;
  isPending: boolean;
  isDisabled: boolean;
  onSelectClinician: (memberId: string) => void;
}

function ClinicianSelectorButton({
  variant,
  buttonVariant,
  size,
  label,
  isPending,
  isDisabled,
  onSelectClinician,
}: ClinicianSelectorButtonProps) {
  const [open, setOpen] = useState(false);

  // Reuse the toolbar CTA's selector so the "self pinned on top (name + avatar),
  // Team group below" presentation stays identical across surfaces (SCR-5441).
  const trigger =
    variant === 'panel' ? (
      <Button
        variant={buttonVariant}
        size={size}
        disabled={isDisabled}
        data-testid="sessions-panel-action-new-session"
      >
        {isPending ? <Loader2 className="animate-spin" /> : <Plus aria-hidden="true" />}
        {label}
      </Button>
    ) : (
      <div>
        <NavItem
          variant="action"
          label={label}
          icon={isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          disabled={isDisabled}
          testId="sidebar-link-new-session"
        />
      </div>
    );

  return (
    <ClinicianSelectorPopover
      host={sessionListHost}
      open={open}
      onOpenChange={setOpen}
      onSelectClinician={onSelectClinician}
      contentAlign="start"
    >
      {trigger}
    </ClinicianSelectorPopover>
  );
}
